import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { SelectList } from "@common/selectList";
import { Calendar } from "../calendar";
import { Props } from "./types";
import { CalendarIcon } from "./calendarIcon";
import styles from "./styles.scss";

// Noon is the safest default time for a date-only pick: no DST transition
// ever crosses noon, so the displayed date is stable across timezones.
const DEFAULT_HOUR = 12;

function DateTimeInput({ calendarPosition = "BOTTOM", ...props }: Props) {
  const zone = props.projectTimezone || DateTime.local().zoneName;
  const display = props.dateTime
    ? DateTime.fromISO(props.dateTime, { zone: "utc" }).setZone(zone)
    : null;
  const isValid = !!display && display.isValid;
  // The label can be shown in another zone (the viewer's) while the calendar
  // and the committed date stay in the project zone.
  const displayLabel = isValid
    ? display
        .setZone(props.displayTimezone || zone)
        .toLocaleString(DateTime.DATE_SHORT)
    : "";

  const commitDate = (year: number, month: number, day: number) => {
    const next = DateTime.fromObject(
      {
        year,
        month: month + 1,
        day,
        hour: DEFAULT_HOUR,
        minute: 0,
        second: 0,
        millisecond: 0,
      },
      { zone },
    );
    if (!next.isValid) return;
    props.input(next.toUTC().toISO() ?? "");
  };

  return props.readOnly ? (
    <div
      className={classNames(styles.dateTimeInput, {
        [styles.invalid]: props.isInvalid,
      })}
    >
      <div className={classNames(styles.main, styles.readOnly)}>
        <input
          className={styles.input}
          value={displayLabel}
          placeholder={props.placeholder}
          readOnly
        />
        {props.showCalendarIcon !== false && (
          <CalendarIcon className={styles.icon} />
        )}
      </div>
    </div>
  ) : (
    <SelectList
      listClassName={classNames(styles.calendar, {
        [styles.calendarBottom]: calendarPosition === "BOTTOM",
        [styles.calendarTop]: calendarPosition === "TOP",
        [styles.calendarRight]: calendarPosition === "RIGHT",
        [styles.calendarLeft]: calendarPosition === "LEFT",
      })}
    >
      {({ select }) => ({
        input: (
          <div
            className={classNames(styles.dateTimeInput, {
              [styles.invalid]: props.isInvalid,
            })}
          >
            <div
              className={`${styles.main} ${
                props.showCalendarIcon === false ? styles.noIcon : ""
              }`}
              style={
                props.color ? { border: `1px solid ${props.color}` } : undefined
              }
            >
              <input
                className={styles.input}
                value={displayLabel}
                placeholder={props.placeholder}
                readOnly
              />
              {props.showCalendarIcon !== false && (
                <CalendarIcon
                  className={styles.icon}
                  style={props.color ? { fill: props.color } : undefined}
                />
              )}
            </div>
          </div>
        ),
        list: (
          <Calendar
            date={
              isValid
                ? {
                    year: display.year,
                    month: display.month - 1,
                    day: display.day,
                  }
                : (props.minDate ??
                  props.maxDate ??
                  (() => {
                    const now = DateTime.now().setZone(zone);
                    return {
                      year: now.year,
                      month: now.month - 1,
                      day: now.day,
                    };
                  })())
            }
            minDate={props.minDate}
            maxDate={props.maxDate}
            editDate={(date) => {
              select(true);
              commitDate(date.year, date.month, date.day);
            }}
            clear={
              props.clear
                ? () => {
                    props.clear!();
                    select("");
                  }
                : undefined
            }
            today={() => {
              const todayIso =
                DateTime.now()
                  .setZone(zone)
                  .set({
                    hour: DEFAULT_HOUR,
                    minute: 0,
                    second: 0,
                    millisecond: 0,
                  })
                  .toUTC()
                  .toISO() ?? "";
              props.input(todayIso);
              select("");
            }}
          />
        ),
      })}
    </SelectList>
  );
}

export { DateTimeInput };
